import { useCallback } from "react"
import { useOffDayListQuery } from "./useOffDayListQuery"
import { useRuntimeEnv } from "@harea/component"
import moment from "moment"
import { THoliday } from "@harea/component/dist/types/harea"

export const useDateDifferenceExcludingWeekends = () => {
    const { data: offDaysData } = useOffDayListQuery(true, moment().year())
    const { runtimeEnv } = useRuntimeEnv(true)

    return useCallback((startDate: moment.Moment, endDate: moment.Moment, hourBegin?: string, hourEnd?: string) => {
        if (!startDate || !endDate || startDate.isAfter(endDate)) {
            return 0;
        }

        const lunchStart = runtimeEnv?.organisation?.profile?.workRestHourBegin || "12:00"
        const lunchEnd = runtimeEnv?.organisation?.profile?.workRestHourEnd || "13:00"
        const lunchStartMoment = moment(lunchStart, 'HH:mm')
        const lunchEndMoment = moment(lunchEnd, 'HH:mm')

        const holidayDates = new Set<string>()
        offDaysData?.data?.forEach((holiday: THoliday) => {
            if (holiday.startDate && holiday.endDate && (holiday.startDate !== holiday.endDate)) {
                const current = moment(holiday.startDate)
                const end = moment(holiday.endDate)
                while (current.isSameOrBefore(end, 'day')) {
                    holidayDates.add(current.format('YYYY-MM-DD'))
                    current.add(1, 'day')
                }
            } else if (holiday.startDate) {
                holidayDates.add(moment(holiday.startDate).format('YYYY-MM-DD'))
            }
        })

        let totalDays = 0
        let current = startDate.clone()

        while (current.isSameOrBefore(endDate, 'day')) {
            const dayOfWeek = current.day()
            const dateStr = current.format('YYYY-MM-DD')
            const isWeekend = dayOfWeek === 0 || dayOfWeek === 6
            const isHoliday = holidayDates.has(dateStr)

            if (!isWeekend && !isHoliday) {
                const isFirstDay = current.isSame(startDate, 'day')
                const isLastDay = current.isSame(endDate, 'day')
                let dayValue = 1

                if (isFirstDay || isLastDay) {
                    const hourBeginMoment = hourBegin ? moment(hourBegin, 'HH:mm') : null
                    const hourEndMoment = hourEnd ? moment(hourEnd, 'HH:mm') : null

                    if (isFirstDay && isLastDay) {
                        if (hourBeginMoment && hourEndMoment) {
                            if (hourEndMoment.isAfter(lunchStartMoment) && hourBeginMoment.isBefore(lunchEndMoment) && !startDate.isSame(endDate, 'day')) {
                                dayValue = 1
                            } else if (hourEndMoment.isSameOrAfter(lunchStartMoment)) {
                                dayValue = 0.5
                            } else if (hourEndMoment.isBefore(lunchStartMoment)) {
                                dayValue = 0
                            } else if (hourBeginMoment.isAfter(lunchEndMoment)) {
                                dayValue = 0.5
                            }
                        }
                    } else if (isFirstDay) {
                        if (hourBeginMoment && hourBeginMoment.isAfter(lunchEndMoment)) {
                            dayValue = 0.5
                        }
                    } else if (isLastDay) {
                        if (hourEndMoment && hourEndMoment.isAfter(lunchStartMoment)) {
                            dayValue = 0.5
                        } else if (hourEndMoment && hourEndMoment.isSameOrBefore(lunchStartMoment)) {
                            dayValue = 0
                        }
                    }
                }

                totalDays += dayValue
            }

            current.add(1, 'day')
        }

        return totalDays
    }, [offDaysData?.data, runtimeEnv?.organisation?.profile?.workRestHourBegin, runtimeEnv?.organisation?.profile?.workRestHourEnd])
}
